import React, { useState, useEffect, useRef } from 'react';
import { BaseComponent, Building, PhotoModel, RoomModel } from '../../types/protocols';
import { BaseComponentImpl } from '../../types/implementations';
import { translate } from '../../types/EnumTranslations';
import { uploadImagesForComponent } from '../../Helpers/ImageUploadUtils';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, functions } from '../../firebase';
import { extractExifData, extractExifDataAlternative, getBestExifDate } from '../../Helpers/ExifUtils';
import { getDatabase, ref as dbRef, push, set } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { formatDateForStorage } from '../../Helpers/DateUtils';
import { usePasteUpload } from '../../hooks/usePasteUpload';
import { recordActivity } from '../../api/activityApi';
import { 
    processInBatches, 
    retryWithBackoff, 
    analyzeUploadError, 
    createHeicThumbnail,
    batchUpload,
    UploadProgress,
    UploadError
} from '../../Helpers/UploadUtils';

interface ImageUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (photoModels: PhotoModel[]) => void;
    files: File[];
    component?: BaseComponent;
    room?: RoomModel;
    building: Building;
    buildingKey: string;
}

interface ImagePreview {
    id: string; // Add unique ID for stable keys
    file: File;
    preview: string;
    storagePath: string;
    photoModel: PhotoModel;
    exifData?: any;
    date: Date; // Add date as a separate field for the date picker
    isHeic?: boolean; // True if this is a HEIC file
    needsConversion?: boolean; // True if HEIC needs conversion (deferred until upload)
    uploadProgress?: UploadProgress; // Per-file upload progress
    uploadError?: UploadError; // Per-file upload error
}

interface ConvertHeicResponse {
    success: boolean;
    finalPath?: string;
    error?: string;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    files,
    component,
    room,
    building,
    buildingKey
}) => {
    const [user] = useAuthState(auth);
    const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [overallProgress, setOverallProgress] = useState<{
        current: number;
        total: number;
        currentFileName: string;
    } | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [hoveredDeleteId, setHoveredDeleteId] = useState<string | null>(null);
    const [uploadQueueStatus, setUploadQueueStatus] = useState<'idle' | 'uploading' | 'paused' | 'completed'>('idle');
    
    // Track files currently being processed to prevent duplicates
    const processingFilesRef = useRef<Set<string>>(new Set());
    
    // Check if any uploads are in progress
    const hasActiveUploads = imagePreviews.some(preview => 
        preview.uploadProgress?.status === 'uploading' || 
        preview.uploadProgress?.status === 'converting'
    );

    // formatDateForStorage is now imported from DateUtils

    // Function to format date for datetime-local input (YYYY-MM-DDTHH:mm)
    const formatDateForInput = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Helper function to check if a file is HEIC
    const isHeicFile = (file: File): boolean => {
        return file.type === 'image/heic' || 
               file.type === 'image/heif' || 
               file.name.toLowerCase().endsWith('.heic') || 
               file.name.toLowerCase().endsWith('.heif');
    };

    // Unified function to process any file type for preview
    const processFileForPreview = async (
        file: File,
        index: number
    ): Promise<ImagePreview | null> => {
        // Create unique file identifier to prevent duplicate processing
        const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
        
        // Skip if already processing this file
        if (processingFilesRef.current.has(fileKey)) {
            console.log(`Skipping ${file.name} - already being processed`);
            return null;
        }
        
        // Mark as processing
        processingFilesRef.current.add(fileKey);
        
        try {
            const isHeic = isHeicFile(file);
            const entityType = component ? 'components' : 'rooms';
            const entityKey = component?.key || room?.key;
            
            if (!entityKey) {
                console.error('No entity key found for component or room');
                return null;
            }
            
            // Extract EXIF data (works for both regular and HEIC)
            let exifData;
            try {
                if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
                    exifData = await extractExifData(file);
                    if (!exifData || Object.keys(exifData).length === 0) {
                        exifData = await extractExifDataAlternative(file);
                    }
                } else {
                    exifData = await extractExifData(file);
                    if (isHeic && (!exifData || Object.keys(exifData).length === 0)) {
                        exifData = await extractExifDataAlternative(file);
                    }
                }
            } catch (e) {
                exifData = {};
            }
            
            const exifDate = getBestExifDate(exifData);
            const currentDate = new Date();
            
            // Handle EXIF date properly
            let dateForPicker: Date;
            let finalDate: string;
            
            if (exifDate) {
                const datePart = exifDate.split(' ')[0];
                const timePart = exifDate.split(' ')[1];
                const isoString = datePart.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3') + 'T' + timePart;
                const exifDateObj = new Date(isoString);
                dateForPicker = exifDateObj;
                finalDate = formatDateForStorage(exifDateObj);
            } else {
                dateForPicker = currentDate;
                finalDate = formatDateForStorage(currentDate);
            }
            
            // Generate a temporary key using Firebase push
            const database = getDatabase();
            const photosRef = dbRef(database, `buildings/${buildingKey}/structure/${entityType}/${entityKey}/photos`);
            const newPhotoRef = push(photosRef);
            const tempKey = newPhotoRef.key!;
            
            const storagePath = `buildings/${buildingKey}/structure/${entityType}/${entityKey}/photos/${tempKey}`;
            
            // Create preview - for HEIC, use placeholder or try to create thumbnail
            let preview: string;
            if (isHeic) {
                preview = await createHeicThumbnail(file); // Returns empty string for now
            } else {
                preview = URL.createObjectURL(file);
            }
            
            const photoModel: PhotoModel = {
                key: tempKey,
                author: user?.uid || '',
                date: finalDate,
                width: 0, // Will be set after image loads
                height: 0, // Will be set after image loads
                storagePath: storagePath,
                fileSize: file.size
            };
            
            return {
                id: `preview-${Date.now()}-${index}-${Math.random()}`,
                file,
                preview,
                storagePath,
                photoModel,
                exifData,
                date: dateForPicker,
                isHeic,
                needsConversion: isHeic
            };
        } catch (error) {
            console.error(`Error processing file ${file.name}:`, error);
            return null;
        } finally {
            processingFilesRef.current.delete(fileKey);
        }
    };

    useEffect(() => {
        if (isOpen && files.length > 0 && (component || room)) {
            const processFiles = async () => {
                // Process all files in parallel with concurrency limit
                const results = await processInBatches(
                    files,
                    async (file, index) => await processFileForPreview(file, index),
                    3 // Process 3 files at a time
                );
                
                // Filter out null results and update state
                const validPreviews = results.filter((preview): preview is ImagePreview => preview !== null);
                
                setImagePreviews(prev => {
                    // Merge with existing previews, avoiding duplicates
                    const existingIds = new Set(prev.map(p => p.id));
                    const newPreviews = validPreviews.filter(p => !existingIds.has(p.id));
                    return [...prev, ...newPreviews];
                });
            };

            processFiles();
        }
    }, [isOpen, files, component, room, buildingKey, user]);


    useEffect(() => {
        return () => {
            // Clean up object URLs
            imagePreviews.forEach(preview => {
                URL.revokeObjectURL(preview.preview);
            });
        };
    }, [imagePreviews]);

    const handleImageLoad = (id: string, event: React.SyntheticEvent<HTMLImageElement>) => {
        const img = event.currentTarget;
        setImagePreviews(prev => prev.map(preview => 
            preview.id === id 
                ? {
                    ...preview,
                    photoModel: {
                        ...preview.photoModel,
                        width: img.naturalWidth,
                        height: img.naturalHeight
                    }
                }
                : preview
        ));
    };

    // Upload a single HEIC file with conversion
    const uploadHeicFile = async (
        preview: ImagePreview,
        onProgress: (progress: number) => void
    ): Promise<PhotoModel> => {
        const file = preview.file;
        const tempStoragePath = `temp/heic/${preview.photoModel.key}`;
        const finalStoragePath = preview.storagePath;
        
        // Update progress: uploading
        setImagePreviews(prev => prev.map(p => 
            p.id === preview.id 
                ? {
                    ...p,
                    uploadProgress: {
                        fileId: p.id,
                        fileName: p.file.name,
                        status: 'uploading',
                        progress: 10
                    }
                }
                : p
        ));
        onProgress(10);
        
        // Upload HEIC to temp location
        const storage = getStorage();
        const tempFileRef = storageRef(storage, tempStoragePath);
        await retryWithBackoff(async () => {
            await uploadBytes(tempFileRef, file);
        });
        
        onProgress(30);
        
        // Update progress: converting
        setImagePreviews(prev => prev.map(p => 
            p.id === preview.id 
                ? {
                    ...p,
                    uploadProgress: {
                        fileId: p.id,
                        fileName: p.file.name,
                        status: 'converting',
                        progress: 50
                    }
                }
                : p
        ));
        
        // Call conversion function
        const convertHeic = httpsCallable(functions, 'convertHeicToPng');
        const convertResult = await retryWithBackoff(async () => {
            return await convertHeic({
                tempStoragePath,
                finalStoragePath
            });
        });
        
        const result = convertResult.data as ConvertHeicResponse;
        if (!result.success) {
            throw new Error(result.error || 'HEIC conversion failed');
        }
        
        onProgress(70);
        
        // Download converted PNG to get dimensions
        const finalFileRef = storageRef(storage, finalStoragePath);
        const previewUrl = await getDownloadURL(finalFileRef);
        
        // Get image dimensions
        const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
            };
            img.onerror = () => {
                resolve({ width: 0, height: 0 });
            };
            img.src = previewUrl;
        });
        
        onProgress(90);
        
        // Update PhotoModel with dimensions
        const updatedPhotoModel: PhotoModel = {
            ...preview.photoModel,
            width: dimensions.width,
            height: dimensions.height
        };
        
        // Create database entry
        const database = getDatabase();
        const entityType = component ? 'components' : 'rooms';
        const entityKey = component?.key || room?.key;
        
        if (entityKey) {
            const photoRef = dbRef(database, `buildings/${buildingKey}/structure/${entityType}/${entityKey}/photos/${preview.photoModel.key}`);
            await set(photoRef, updatedPhotoModel);
        }
        
        onProgress(100);
        
        return updatedPhotoModel;
    };

    // Upload a regular file
    const uploadRegularFile = async (
        preview: ImagePreview,
        onProgress: (progress: number) => void
    ): Promise<PhotoModel> => {
        onProgress(10);
        
        if (component) {
            // Upload to component
            const uploadResult = await retryWithBackoff(async () => {
                return await uploadImagesForComponent(
                    buildingKey,
                    component.key,
                    [preview.file],
                    user?.uid || ''
                );
            });
            
            if (uploadResult.success && uploadResult.photoModels && uploadResult.photoModels.length > 0) {
                onProgress(100);
                return uploadResult.photoModels[0];
            } else {
                throw new Error(uploadResult.error || 'Upload failed');
            }
        } else if (room) {
            // Upload to room
            const storage = getStorage();
            const fileRef = storageRef(storage, preview.storagePath);
            
            await retryWithBackoff(async () => {
                await uploadBytes(fileRef, preview.file);
            });
            
            onProgress(80);
            
            // Save the photo model to database
            const database = getDatabase();
            const photoRef = dbRef(database, `buildings/${buildingKey}/structure/rooms/${room.key}/photos/${preview.photoModel.key}`);
            await set(photoRef, preview.photoModel);
            
            onProgress(100);
            return preview.photoModel;
        } else {
            throw new Error('No component or room specified');
        }
    };

    const handleSubmit = async () => {
        if (uploadQueueStatus === 'uploading') {
            return; // Already uploading
        }
        
        setIsUploading(true);
        setUploadQueueStatus('uploading');
        setOverallProgress({
            current: 0,
            total: imagePreviews.length,
            currentFileName: ''
        });

        const uploadedPhotoModels: PhotoModel[] = [];
        let successCount = 0;
        let failureCount = 0;

        try {
            // Upload files in batches with progress tracking
            await batchUpload(
                imagePreviews,
                async (preview: ImagePreview, onProgress: (progress: number) => void) => {
                    const previewId = preview.id;
                    
                    try {
                        // Update overall progress
                        setOverallProgress(prev => prev ? {
                            ...prev,
                            current: prev.current + 1,
                            currentFileName: preview.file.name
                        } : null);
                        
                        // Update per-file progress: pending -> uploading
                        setImagePreviews(prev => prev.map(p => 
                            p.id === previewId 
                                ? {
                                    ...p,
                                    uploadProgress: {
                                        fileId: p.id,
                                        fileName: p.file.name,
                                        status: 'uploading',
                                        progress: 0
                                    }
                                }
                                : p
                        ));
                        
                        let photoModel: PhotoModel;
                        
                        if (preview.isHeic && preview.needsConversion) {
                            // HEIC file - convert during upload
                            photoModel = await uploadHeicFile(preview, onProgress);
                        } else {
                            // Regular file
                            photoModel = await uploadRegularFile(preview, onProgress);
                        }
                        
                        // Update progress: completed
                        setImagePreviews(prev => prev.map(p => 
                            p.id === previewId 
                                ? {
                                    ...p,
                                    uploadProgress: {
                                        fileId: p.id,
                                        fileName: p.file.name,
                                        status: 'completed',
                                        progress: 100
                                    },
                                    uploadError: undefined
                                }
                                : p
                        ));
                        
                        uploadedPhotoModels.push(photoModel);
                        successCount++;
                    } catch (error) {
                        const uploadError = analyzeUploadError(
                            error instanceof Error ? error : new Error(String(error)),
                            preview.file.name,
                            previewId
                        );
                        
                        // Update progress: failed
                        setImagePreviews(prev => prev.map(p => 
                            p.id === previewId 
                                ? {
                                    ...p,
                                    uploadProgress: {
                                        fileId: p.id,
                                        fileName: p.file.name,
                                        status: 'failed',
                                        progress: 0,
                                        error: uploadError.error
                                    },
                                    uploadError
                                }
                                : p
                        ));
                        
                        failureCount++;
                        console.error(`Failed to upload ${preview.file.name}:`, error);
                    }
                },
                4 // Batch size: upload 4 files at a time
            );

            // Show summary
            if (successCount > 0) {
                // One entry for the whole batch
                recordActivity('photo.uploaded', { buildingKey, targetKey: component?.key ?? room?.key });
                onSubmit(uploadedPhotoModels);
                
                if (failureCount > 0) {
                    alert(`${successCount} bilder laddades upp framgångsrikt. ${failureCount} bilder misslyckades.`);
                }
            } else {
                throw new Error('Alla uppladdningar misslyckades');
            }

        } catch (error) {
            console.error('Upload process failed:', error);
            alert(`Uppladdning misslyckades: ${error instanceof Error ? error.message : 'Okänt fel'}`);
        } finally {
            setIsUploading(false);
            setUploadQueueStatus('completed');
            setOverallProgress(null);
        }
    };

    const handleDateChange = (id: string, newDate: Date) => {
        setImagePreviews(prev => prev.map(preview => 
            preview.id === id 
                ? {
                    ...preview,
                    date: newDate,
                    photoModel: {
                        ...preview.photoModel,
                        date: formatDateForStorage(newDate)
                    }
                }
                : preview
        ));
    };

    const handleDeleteRow = (id: string) => {
        setImagePreviews(prev => {
            const previewToDelete = prev.find(p => p.id === id);
            if (previewToDelete) {
                // Clean up the object URL for the deleted image
                URL.revokeObjectURL(previewToDelete.preview);
            }
            return prev.filter(p => p.id !== id);
        });
    };

    // Drag and drop handlers for the modal
    const handleModalDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleModalDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set drag over to false if we're leaving the modal entirely
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragOver(false);
        }
    };

    // Shared ingest for dropped and pasted files: filters to images, builds previews.
    const addImageFiles = async (allFiles: File[]) => {
        setErrorMessage(null); // Clear any previous error

        const invalidFiles = allFiles.filter(file =>
            !file.type.startsWith('image/')
        );

        if (invalidFiles.length > 0) {
            setErrorMessage('Ogiltigt filformat');
            setTimeout(() => {
                setErrorMessage(null);
            }, 3000);
        }

        const validFiles = allFiles.filter(file => file.type.startsWith('image/'));

        if (validFiles.length > 0) {
            // Process files in parallel using the unified function
            const results = await processInBatches(
                validFiles,
                async (file, index) => await processFileForPreview(file, imagePreviews.length + index),
                3 // Process 3 files at a time
            );

            // Filter out null results and update state
            const validPreviews = results.filter((preview): preview is ImagePreview => preview !== null);

            setImagePreviews(prev => {
                // Merge with existing previews, avoiding duplicates
                const existingIds = new Set(prev.map(p => p.id));
                const newPreviews = validPreviews.filter(p => !existingIds.has(p.id));
                return [...prev, ...newPreviews];
            });
        }
    };

    const handleModalDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        await addImageFiles(Array.from(e.dataTransfer.files));
    };

    // Allow pasting images (Ctrl/Cmd + V) while the modal is open and not uploading.
    usePasteUpload({
        enabled: isOpen && !isUploading,
        onFilesPasted: (files) => { addImageFiles(files); }
    });

    if (!isOpen) return null;

    const componentImpl = component ? new BaseComponentImpl(component) : null;
    const targetName = component 
        ? (componentImpl?.displayName(building) || 'Komponent')
        : (room?.label || translate(room?.type || 'room_general') || 'Rum');

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}
            onClick={isUploading ? undefined : onClose}
        >
            <div 
                style={{
                    backgroundColor: isDragOver ? '#f0f8ff' : 'white',
                    borderRadius: '10px',
                    padding: '30px',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                    border: isDragOver ? '3px dashed #007bff' : '3px solid transparent',
                    transition: 'all 0.3s ease'
                }}
                onClick={(e) => e.stopPropagation()}
                onDragOver={handleModalDragOver}
                onDragLeave={handleModalDragLeave}
                onDrop={handleModalDrop}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '15px'
                }}>
                    <h2 style={{ margin: 0, color: '#333' }}>
                        {`Ladda upp bilder - ${targetName}`}
                    </h2>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                        <><strong>{component ? 'Komponent' : 'Rum'}:</strong> {targetName}</>
                    </p>
                    {component && (
                        <>
                            <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                                <strong>Typ:</strong> {translate(component.type)}
                            </p>
                            {component.cardinalDirection?.value && (
                                <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                                    <strong>Väderstreck:</strong> {translate(component.cardinalDirection.value)}
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Paste / drop hint */}
                <p style={{ margin: '0 0 20px 0', color: '#888', fontSize: '13px' }}>
                    Dra och släpp bilder i rutan eller klistra in med Ctrl + V
                </p>

                {/* Error Message */}
                {errorMessage && (
                    <div style={{
                        marginBottom: '20px',
                        padding: '12px 16px',
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        border: '1px solid #f5c6cb',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500',
                        textAlign: 'center'
                    }}>
                        {errorMessage}
                    </div>
                )}

                {/* Drag and drop indicator */}
                {isDragOver && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0, 123, 255, 0.9)',
                        color: 'white',
                        padding: '20px 40px',
                        borderRadius: '10px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        zIndex: 1001,
                        pointerEvents: 'none',
                        boxShadow: '0 4px 20px rgba(0, 123, 255, 0.3)'
                    }}>
                        📁 Släpp bilder här för att lägga till dem
                    </div>
                )}

                {!isUploading && (
                    <div style={{ marginBottom: '30px' }}>
                        {imagePreviews.map((preview, index) => (
                        <div key={preview.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px',
                            padding: '15px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            backgroundColor: '#fafafa'
                        }}>
                            {/* Thumbnail */}
                            <div style={{ flexShrink: 0, position: 'relative' }}>
                                {!preview.preview && preview.isHeic ? (
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '5px',
                                        backgroundColor: '#e9ecef',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #dee2e6',
                                        flexDirection: 'column',
                                        gap: '4px'
                                    }}>
                                        <span style={{ fontSize: '24px' }}>📷</span>
                                        <span style={{ fontSize: '10px', color: '#666' }}>HEIC</span>
                                    </div>
                                ) : preview.uploadProgress?.status === 'uploading' || preview.uploadProgress?.status === 'converting' ? (
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '5px',
                                        backgroundColor: '#e9ecef',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #dee2e6',
                                        flexDirection: 'column',
                                        gap: '4px'
                                    }}>
                                        <div style={{
                                            width: '24px',
                                            height: '24px',
                                            border: '3px solid #f3f3f3',
                                            borderTop: '3px solid #007bff',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }} />
                                        <span style={{ fontSize: '10px', color: '#666' }}>
                                            {preview.uploadProgress.progress}%
                                        </span>
                                        <style>{`
                                            @keyframes spin {
                                                0% { transform: rotate(0deg); }
                                                100% { transform: rotate(360deg); }
                                            }
                                        `}</style>
                                    </div>
                                ) : preview.uploadProgress?.status === 'failed' ? (
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '5px',
                                        backgroundColor: '#f8d7da',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #f5c6cb',
                                        flexDirection: 'column',
                                        gap: '4px'
                                    }}>
                                        <span style={{ fontSize: '24px' }}>❌</span>
                                        <span style={{ fontSize: '10px', color: '#721c24' }}>Fel</span>
                                    </div>
                                ) : preview.preview ? (
                                    <img
                                        src={preview.preview}
                                        alt={`Preview ${index + 1}`}
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            objectFit: 'cover',
                                            borderRadius: '5px'
                                        }}
                                        onLoad={(e) => handleImageLoad(preview.id, e)}
                                    />
                                ) : (
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '5px',
                                        backgroundColor: '#e9ecef',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #dee2e6'
                                    }}>
                                        <span style={{ fontSize: '24px' }}>📷</span>
                                    </div>
                                )}
                            </div>

                            {/* File info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ marginBottom: '5px' }}>
                                    <strong style={{ fontSize: '14px' }}>{preview.file.name}</strong>
                                    {preview.isHeic && (
                                        <span style={{ 
                                            fontSize: '10px', 
                                            color: '#007bff', 
                                            marginLeft: '8px',
                                            backgroundColor: '#e7f3ff',
                                            padding: '2px 6px',
                                            borderRadius: '4px'
                                        }}>
                                            HEIC
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {(preview.file.size / 1024 / 1024).toFixed(2)} MB
                                    {preview.photoModel.width > 0 && preview.photoModel.height > 0 && (
                                        <span> • {preview.photoModel.width} × {preview.photoModel.height} px</span>
                                    )}
                                </div>
                                {preview.uploadProgress && (
                                    <div style={{ marginTop: '4px', fontSize: '11px' }}>
                                        {preview.uploadProgress.status === 'uploading' && (
                                            <span style={{ color: '#007bff' }}>Laddar upp... {preview.uploadProgress.progress}%</span>
                                        )}
                                        {preview.uploadProgress.status === 'converting' && (
                                            <span style={{ color: '#ff9800' }}>Konverterar... {preview.uploadProgress.progress}%</span>
                                        )}
                                        {preview.uploadProgress.status === 'completed' && (
                                            <span style={{ color: '#28a745' }}>✓ Klar</span>
                                        )}
                                        {preview.uploadProgress.status === 'failed' && (
                                            <span style={{ color: '#dc3545' }}>
                                                ✗ {preview.uploadError?.error || 'Fel'}
                                                {preview.uploadError?.suggestion && (
                                                    <div style={{ fontSize: '10px', marginTop: '2px', color: '#666' }}>
                                                        {preview.uploadError.suggestion}
                                                    </div>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Date picker */}
                            <div style={{ flexShrink: 0 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>
                                    Datum:
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formatDateForInput(preview.date)}
                                    onChange={(e) => handleDateChange(preview.id, new Date(e.target.value))}
                                    disabled={isUploading}
                                    style={{
                                        padding: '8px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        minWidth: '180px',
                                        backgroundColor: isUploading ? '#f5f5f5' : 'white',
                                        cursor: isUploading ? 'not-allowed' : 'text'
                                    }}
                                />
                                {preview.exifData && (preview.exifData.dateTimeOriginal || preview.exifData.dateTimeDigitized || preview.exifData.dateTime) && (
                                    <div style={{ fontSize: '10px', color: '#28a745', marginTop: '2px' }}>
                                        (från EXIF)
                                    </div>
                                )}
                            </div>

                            {/* Delete button */}
                            <div style={{ flexShrink: 0 }}>
                                <button
                                    onClick={() => handleDeleteRow(preview.id)}
                                    disabled={isUploading}
                                    title="Ta bort"
                                    onMouseEnter={() => setHoveredDeleteId(preview.id)}
                                    onMouseLeave={() => setHoveredDeleteId(null)}
                                    style={{
                                        background: 'white',
                                        color: isUploading ? '#ccc' : '#6c757d',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '30px',
                                        height: '30px',
                                        padding: '0',
                                        cursor: isUploading ? 'not-allowed' : 'pointer',
                                        fontSize: '28px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        lineHeight: 1,
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                        transform: hoveredDeleteId === preview.id ? 'scale(1.2)' : 'scale(1)',
                                        transition: 'transform 0.15s ease'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    ))}
                    </div>
                )}

                {imagePreviews.length > 0 ? (
                    <div>
                        {/* Progress Bar */}
                        {overallProgress && (
                            <div style={{
                                marginBottom: '20px',
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '10px'
                                }}>
                                    <span style={{ fontWeight: 'bold', color: '#333' }}>
                                        Laddar upp bilder...
                                    </span>
                                    <span style={{ color: '#666', fontSize: '14px' }}>
                                        {overallProgress.current} av {overallProgress.total}
                                    </span>
                                </div>
                                
                                <div style={{
                                    width: '100%',
                                    height: '8px',
                                    backgroundColor: '#e9ecef',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    marginBottom: '8px'
                                }}>
                                    <div style={{
                                        width: `${(overallProgress.current / overallProgress.total) * 100}%`,
                                        height: '100%',
                                        backgroundColor: '#007bff',
                                        transition: 'width 0.3s ease'
                                    }} />
                                </div>
                                
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {overallProgress.currentFileName || 'Förbereder uppladdning...'}
                                </div>
                            </div>
                        )}

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                            borderTop: '1px solid #eee',
                            paddingTop: '20px'
                        }}>
                        <button
                            onClick={onClose}
                            disabled={isUploading}
                            style={{
                                padding: '10px 20px',
                                border: '1px solid #ccc',
                                borderRadius: '5px',
                                backgroundColor: 'white',
                                cursor: isUploading ? 'not-allowed' : 'pointer',
                                color: '#666'
                            }}
                        >
                            Avbryt
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isUploading || hasActiveUploads}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '5px',
                                backgroundColor: (isUploading || hasActiveUploads) ? '#ccc' : '#001D60',
                                color: 'white',
                                cursor: (isUploading || hasActiveUploads) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isUploading 
                                ? 'Laddar upp...' 
                                : hasActiveUploads
                                    ? 'Bearbetar bilder...' 
                                    : `Ladda upp ${imagePreviews.length} bilder`}
                        </button>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '16px',
                        borderTop: '1px solid #eee',
                        paddingTop: '20px'
                    }}>
                        <p style={{ marginBottom: '20px' }}>Inga bilder att ladda upp</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={onClose}
                                style={{
                                    padding: '10px 20px',
                                    border: '1px solid #ccc',
                                    borderRadius: '5px',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    color: '#666'
                                }}
                            >
                                Avbryt
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageUploadModal;
